import React from "react";
import { cn } from "../../utils/cn";

interface EmptyStateProps {
  icon?: React.ReactNode;
  title: string;
  description?: string;
  action?: React.ReactNode;
  size?: "sm" | "md" | "lg";
  className?: string;
}

const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  action,
  size = "md",
  className,
}) => {
  const sizeClasses = {
    sm: {
      icon: "h-8 w-8",
      title: "text-base",
      description: "text-sm",
    },
    md: {
      icon: "h-12 w-12",
      title: "text-lg",
      description: "text-sm",
    },
    lg: {
      icon: "h-16 w-16",
      title: "text-xl",
      description: "text-base",
    },
  };

  const styles = sizeClasses[size];

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center py-12 sm:py-16 px-4 text-center",
        className,
      )}
      role="status"
      aria-live="polite"
    >
      {icon && (
        <div
          className={cn(
            "text-gray-400 mb-4 sm:mb-6 opacity-60 flex items-center justify-center",
            styles.icon,
          )}
          aria-hidden="true"
        >
          {icon}
        </div>
      )}

      <h3
        className={cn(
          "font-semibold text-gray-900 mb-2 sm:mb-3 text-base sm:text-lg",
          styles.title,
        )}
      >
        {title}
      </h3>

      {description && (
        <p
          className={cn(
            "text-gray-500 max-w-md mb-6 sm:mb-8 leading-relaxed text-sm sm:text-base",
            styles.description,
          )}
        >
          {description}
        </p>
      )}

      {action && <div className="mt-2 sm:mt-4">{action}</div>}
    </div>
  );
};

export default EmptyState;
